import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { Skeleton } from '@goaltree/ui/components/skeleton'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { CalendarDays, Check, ChevronDown, Flame } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'
import { celebrate } from '@/lib/celebrate'
import { localDayNumber, useHumanTouch } from '@/lib/human-touch'
import { uncompleteNode } from '@/lib/nodes-optimistic'
import { emitPositiveMoment } from '@/lib/positive-moment'
import { useTodayAllDoneShown } from '@/lib/today-all-done-shown'

function Dot({ category }: { category: string }) {
  const fill = useCategoryFill()
  return (
    <span
      className="size-1.5 shrink-0 rounded-full"
      style={{ background: fill(CATEGORY_COLORS[category.toLowerCase()] ?? '#888') }}
    />
  )
}

// Same footprint as the loaded recap, so nothing jumps when it arrives.
function TodayAllDoneSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <div className="flex items-center gap-4 rounded-2xl border border-border/60 px-4 py-3.5">
        <Skeleton className="size-11 rounded-xl" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-5 w-32 rounded-md" />
          <Skeleton className="h-3 w-48 rounded-md" />
        </div>
        <div className="hidden gap-1.5 sm:flex">
          {Array.from({ length: 7 }, (_, i) => (
            <Skeleton key={i} className="size-5.5 rounded-md" />
          ))}
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-border/60">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <Skeleton className="h-4 w-28 rounded-md" />
          <Skeleton className="h-3 w-20 rounded-md" />
        </div>
        {[72, 56, 64].map((w) => (
          <div
            key={w}
            className="flex items-center gap-3 border-b border-border/40 px-4 py-3 last:border-b-0"
          >
            <Skeleton className="size-4.5 rounded-full" />
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton className="h-3.5 rounded-md" style={{ width: `${w}%` }} />
              <Skeleton className="h-2.5 w-32 rounded-md" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3 rounded-2xl border border-border/60 px-4 py-3.5">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24 rounded-md" />
          <Skeleton className="h-3 w-36 rounded-md" />
        </div>
        {[68, 52].map((w) => (
          <div key={w} className="flex items-center gap-2.5">
            <Skeleton className="size-4 rounded-full" />
            <Skeleton className="h-3.5 rounded-md" style={{ width: `${w}%` }} />
          </div>
        ))}
        <div className="flex gap-2 pt-0.5">
          <Skeleton className="h-8 w-32 rounded-full" />
          <Skeleton className="h-8 w-28 rounded-full" />
        </div>
      </div>
    </div>
  )
}

// The wrap-up Today shows once everything is finished: streak, what got done,
// a preview of tomorrow, and an optional "pull more" from a specific goal.
const BIG_KEY = 'goaltree-day-celebrated'

export function TodayAllDone({
  localDate,
  streak,
  emptyDay = false,
}: {
  localDate: string
  streak: number
  emptyDay?: boolean
}) {
  const { t, i18n } = useTranslation()
  const recap = useQuery(api.dayRecap.getDayRecap, { localDate })
  const uncomplete = useMutation(api.nodes.uncomplete).withOptimisticUpdate(
    (localStore, { taskId }) => {
      uncompleteNode(localStore, taskId)
      const cur = localStore.getQuery(api.dayRecap.getDayRecap, { localDate })
      if (cur)
        localStore.setQuery(
          api.dayRecap.getDayRecap,
          { localDate },
          { ...cur, done: cur.done.filter((d) => d.id !== taskId) },
        )
    },
  )
  const pullMore = useMutation(api.dayRecap.pullMore)
  const addToToday = useMutation(api.dailySurface.addToToday).withOptimisticUpdate(
    (localStore, { taskId }) => {
      const pick = localStore
        .getQuery(api.dayRecap.getDayRecap, { localDate })
        ?.tomorrow.tasks.find((tk) => tk.id === taskId)
      const today = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (!pick || !today || today.tasks.some((tk) => tk.taskId === taskId)) return
      localStore.setQuery(
        api.dailySurface.getTodayWithContext,
        { localDate },
        {
          ...today,
          allCompleted: false,
          tasks: [
            ...today.tasks,
            {
              taskId,
              title: pick.title,
              description: null,
              tags: [],
              goalTitle: pick.goalTitle,
              goalId: pick.goalId,
              milestoneTitle: '',
              milestoneId: null,
              targetTitle: '',
              targetId: null,
              category: pick.category,
              completedAt: null,
              effort: 'medium',
              status: 'backlog',
              isInbox: false,
              dueDate: null,
              recurs: false,
              recursPeriod: null,
              recursDays: null,
              recurrenceDay: null,
              recurrenceTime: null,
              durationMinutes: null,
              allDay: false,
              mustDo: false,
              putOffDays: 0,
            },
          ],
        },
      )
    },
  )
  const [pulling, setPulling] = useState(false)
  const touch = useHumanTouch()
  const setShown = useTodayAllDoneShown((st) => st.set)
  useEffect(() => {
    setShown(true)
    emitPositiveMoment()
    // Big celebration once per day, not on every revisit of a finished Today.
    try {
      if (localStorage.getItem(BIG_KEY) !== localDate) {
        localStorage.setItem(BIG_KEY, localDate)
        celebrate({ x: window.innerWidth / 2, y: window.innerHeight / 2, big: true })
      }
    } catch {}
    return () => setShown(false)
  }, [setShown, localDate])
  if (!recap) return <TodayAllDoneSkeleton />

  const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: 'narrow', timeZone: 'UTC' })
  const tomorrowLabel = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${recap.tomorrow.date}T12:00:00Z`))
  const doneTasks = recap.done.filter((d) => !d.isHabit).length
  const doneHabits = recap.done.length - doneTasks

  async function pull(goalId: Id<'nodes'>) {
    setPulling(true)
    try {
      const n = await pullMore({ goalId, localDate })
      if (n) toast.success(t('today.recap.pulled', { count: n }))
      else toast(t('today.recap.nothingToPull'))
    } finally {
      setPulling(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-orange-500/25 bg-linear-to-br from-orange-500/12 to-orange-500/0 px-4 py-3.5">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-orange-950">
          <Flame className="size-6" fill="currentColor" strokeWidth={0} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xl font-black tabular-nums text-foreground">
            {t('today.recap.streak', { count: streak })}
          </p>
          <p className="text-xs text-muted-foreground">
            {streak > 0
              ? t('today.recap.streakNext', { next: streak + 1 })
              : t(emptyDay ? 'today.recap.streakStartToday' : 'today.recap.streakStart')}
          </p>
        </div>
        <div className="flex gap-1.5">
          {recap.week.map((d, i) => {
            const isToday = d.date === localDate
            const isNext = d.date === recap.tomorrow.date
            return (
              <div
                key={d.date}
                className={cn(
                  'flex flex-col items-center gap-1 text-[10px] font-bold',
                  isToday ? 'text-foreground' : 'text-muted-foreground/60',
                )}
              >
                <span
                  className={cn(
                    'block size-5.5 rounded-md border',
                    d.done ? 'border-orange-500 bg-orange-500' : 'border-border bg-muted/40',
                    isNext && !d.done && 'border-dashed border-orange-500/50',
                    isToday && 'ring-2 ring-orange-500 ring-offset-2 ring-offset-background',
                  )}
                />
                {weekday.format(new Date(`${d.date}T12:00:00Z`)) || i}
              </div>
            )
          })}
        </div>
      </div>
      <p className="-mt-2 text-center text-xs italic text-muted-foreground/70">
        {touch.line(localDayNumber())}
      </p>

      {recap.done.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-border/60 bg-card">
          <header className="flex items-center justify-between border-b border-border/60 px-4 py-3">
            <h2 className="flex items-center gap-2 text-sm font-extrabold text-foreground">
              <Check className="size-4 text-primary-text" strokeWidth={3} />
              {t('today.recap.done', { count: recap.done.length })}
            </h2>
            <span className="text-xs text-muted-foreground">
              {[
                doneTasks ? t('today.recap.tasks', { count: doneTasks }) : null,
                doneHabits ? t('today.recap.habits', { count: doneHabits }) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </header>
          <ul className="divide-y divide-border/60">
            {recap.done.map((d) => (
              <li key={d.id} className="group flex items-center gap-3 px-4 py-2.5">
                <span
                  className={cn(
                    'flex size-4.5 shrink-0 items-center justify-center',
                    d.isHabit ? 'rounded-md bg-orange-500' : 'rounded-full bg-primary',
                  )}
                >
                  <Check className="size-3 text-[#0a0a0a]" strokeWidth={3.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-muted-foreground line-through decoration-muted-foreground/40">
                    {d.title}
                  </p>
                  <p className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground/60">
                    <Dot category={d.category} />
                    <span className="truncate">
                      {d.goalTitle ?? (d.isInbox ? t('nav.inbox') : t('today.recap.habit'))}
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void uncomplete({ taskId: d.id })}
                  className="cursor-pointer text-xs text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
                >
                  {t('common.undo')}
                </button>
              </li>
            ))}
          </ul>
          {recap.gains.length > 0 && (
            <div className="flex flex-wrap gap-1.5 border-t border-border/60 bg-muted/20 px-4 py-2.5">
              {recap.gains.map((g) => (
                <span
                  key={g.goalId}
                  className="flex items-center gap-1.5 rounded-full border border-border/60 px-2.5 py-0.5 text-[11.5px] tabular-nums text-foreground"
                >
                  <Dot category={g.category} />
                  <span className="max-w-48 truncate">{g.title}</span>
                  <b className="font-bold text-primary-text">+{g.pct}%</b>
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="flex flex-col gap-2.5 rounded-2xl border border-border/60 bg-card px-4 py-3.5">
        <header className="flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-sm font-extrabold text-foreground">
            <CalendarDays className="size-4 text-sky-500" />
            {t('today.recap.tomorrow')}
          </h2>
          <span className="truncate text-xs text-muted-foreground">
            {[
              tomorrowLabel,
              recap.tomorrow.tasks.length
                ? t('today.recap.tasks', { count: recap.tomorrow.tasks.length })
                : null,
              recap.tomorrow.habitCount
                ? t('today.recap.habits', { count: recap.tomorrow.habitCount })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </header>
        {recap.tomorrow.tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('today.recap.tomorrowEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {recap.tomorrow.tasks.map((tk, i) => (
              <li
                key={tk.id}
                className={cn(
                  'flex items-center gap-2.5 text-sm',
                  i === 0 ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                <span className="size-4 shrink-0 rounded-full border-[1.5px] border-muted-foreground/50" />
                <span className="min-w-0 flex-1 truncate">{tk.title}</span>
                <span className="hidden max-w-40 shrink-0 items-center gap-1.5 truncate font-mono text-[11px] text-muted-foreground/60 sm:flex">
                  <Dot category={tk.category} />
                  <span className="truncate">{tk.goalTitle}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-2 pt-0.5">
          <button
            type="button"
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent('open-coach', {
                  detail: { message: t('today.recap.planTomorrowPrompt') },
                }),
              )
            }
            className="cursor-pointer rounded-full bg-primary px-3.5 py-2 text-[13px] font-bold text-primary-foreground transition-opacity hover:opacity-90"
          >
            {t('today.recap.planTomorrow')}
          </button>
          {recap.tomorrow.tasks[0] && (
            <button
              type="button"
              onClick={() => void addToToday({ taskId: recap.tomorrow.tasks[0].id, localDate })}
              className="cursor-pointer rounded-full border border-border bg-muted/40 px-3.5 py-2 text-[13px] font-bold text-foreground transition-colors hover:bg-muted"
            >
              {t('today.recap.startNow')}
            </button>
          )}
        </div>
      </section>

      {recap.pullFrom && (
        <div className="flex flex-wrap items-center justify-center gap-2.5 pt-1 text-[12.5px] text-muted-foreground">
          <span>{t('today.recap.moreEnergy')}</span>
          <div className="flex overflow-hidden rounded-full border border-border">
            <button
              type="button"
              disabled={pulling}
              onClick={() => void pull(recap.pullFrom!.goalId)}
              className="max-w-72 cursor-pointer truncate px-3.5 py-1.5 text-[13px] font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-50"
            >
              {t('today.recap.pullFrom', { goal: recap.pullFrom.title })}
            </button>
            {recap.pullGoals.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  disabled={pulling}
                  aria-label={t('today.recap.pullPick')}
                  className="cursor-pointer border-l border-border px-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <ChevronDown className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>{t('today.recap.pullPick')}</DropdownMenuLabel>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  {recap.pullGoals.map((g) => (
                    <DropdownMenuItem key={g.goalId} onClick={() => void pull(g.goalId)}>
                      <span className="truncate">{g.title}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
